<script>
import {
    fade
} from "svelte/transition";
import {
    modalState, hideModal, showModal
} from "@app/store/modalStore.svelte.js";
import {
    environment
} from "@app/store/envStore.svelte.js";
import {
    authState
} from "@app/store/authStore.svelte.js";
import {
    knpState, loadKnpGuides
} from "@app/store/knpStore.svelte.js";
import {
    toast } from '@zerodevx/svelte-toast';
import {
    v4 as uuidv4
} from "uuid";

import tcLib from "@cclib/lib/timecode.js";

/* Firebase */
import firebase from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import storage from "@app/configs/storage.js";

/* Language Settings */
import assemblyAiLanguages from "@cclib/providers/assemblyAi/languages.js";
import deepgramLanguages from "@cclib/providers/deepgram/languages.js";
import revAiLanguages from "@cclib/providers/revAi/languages.js";
import googleLanguages from "@cclib/providers/googleSpeechToText/languages.js";
import speechmaticsLanguages from "@cclib/providers/speechmatics/languages.js";
import voiceGainLanguages from "@cclib/providers/voicegain/languages.js";
import elevenLabsScribeLanguages from "@cclib/providers/elevenLabsScribe/languages.js";
import * as m from '@app/paraglide/messages.js';

let files = $state([]),
    provider = $state("ElevenLabs Scribe"),
    language = $state("eng"),
    selectedKnpGuide = $state(null),
    totalFiles = 0,
    interactionType = "PROFESSIONALLY_PRODUCED",
    speakers = 2,
    processing = $state(false),
    progress = $state(0),
    userId = firebase.auth().currentUser.uid,
    userEmail = firebase.auth().currentUser.email,
    teamId = authState.team ? authState.team.id : null,
    homeRef = authState.team ? db.collection("teams").doc(teamId).collection("jobs") : db.collection("users").doc(userId).collection("jobs"),
    storageRef = storage.ref(),
    uploadBasePath = authState.team ? "teams/" + teamId + "/watch/video/" : "users/" + userId + "/uploads/",
    audioUploadBasePath = authState.team ? "teams/" + teamId + "/watch/audio/" : "users/" + userId + "/audio/";

// Handler for file inputs
function handleFileInput(event) {
    files = Array.from(event.currentTarget.files || []).map(file => ({
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        jobId: null,
        registered: null,
        progress: 0,
        path: null,
        audioPath: null,
        duration: null
    }));
}

function start() {
    processing = true;
    totalFiles = files.length;
    processJob(0);
}

async function processJob(index) {
    updateProgress(index);
    files[index].jobId = uuidv4();
    files[index].registered = 'in progress';

    if (environment.electron) {        
        files[index].path = await window.webUtils.getPathForFile(files[index].file);
        extractAudio(index);
    } else {
        registerJob(index);
    }
}

function extractAudio(index) {
    try {
        const os = window.os;
        const path = window.path;
        const ffmpeg = window.ffmpeg;

        files[index].audioPath = os.tmpdir() + path.sep + files[index].jobId + ".wav";

        ffmpeg(files[index].path)
            .audioCodec("pcm_mulaw")
            .audioChannels(1)
            .audioFrequency(44100)
            .output(files[index].audioPath)
            .on("start", function(commandLine) {
                console.log("Spawned Ffmpeg with command: " + commandLine);
            })
            .on("codecData", function(data) {
                console.log(data.duration);
                files[index].duration = data.duration;
                console.log("Input is " + data.audio + " audio " + "with " + data.video + " video");
            })
            .on("progress", function(prog) {
                /* prog = {percent, timemark, currentFps, frames, targetSize, currentKbps} */
                console.log("Scanning: " + prog.timemark + " done");
            })
            .on("end", function() {
                registerJob(index);
            })
            .on("error", function(err, stdout, stderr) {
                console.log(err, err.message);

                toast.push(m.failed_to_extract_audio(), {
                    classes: ["toast-danger"]
                });

                files[index].registered = 'failed';

                if (index === totalFiles - 1) {
                    end();
                } else {
                    processJob(index + 1);
                }
            })
            .run();
    } catch (err) {
        console.log(err, err.message);
    }
}

async function registerJob(index) {
    try {
        let jobInfo = {
            id: files[index].jobId,
            projectId: files[index].jobId,
            projectName: files[index].name,
            submittedBy: userEmail,
            userId: userId,
            extId: null,
            progress: 0,
            duration: files[index].duration ? tcLib.tcMsToSec(files[index].duration) : 0,
            cost: 0,
            type: "transcription",
            statusMsg: "Awaiting Media",
            status: "Submitted",
            createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
            updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
            completedOn: null,
            deleted: false,
            config: {
                provider: provider,
                language: language,
                speakers: speakers,
                interactionType: interactionType,
                knpGuideId: selectedKnpGuide,
            }
        };

        await homeRef.doc(jobInfo.id).set(jobInfo);
        files[index].registered = 'done';
        uploadFile(index);
    } catch (err){
        console.log(err,err.message);
        files[index].registered = 'failed';

        if (index === totalFiles - 1) {
            end();
        } else {
            processJob(index + 1);
        }
    }

}

async function uploadFile(index) {
    let uploadTask;
    if (files[index].audioPath){
        let fileResponse = await fetch(files[index].audioPath);
        let byteArrayFileRes = await fileResponse.arrayBuffer();
        uploadTask = storageRef.child((environment.electron ? audioUploadBasePath : uploadBasePath) + files[index].jobId).put(byteArrayFileRes);
    } else {
        uploadTask = storageRef.child((environment.electron ? audioUploadBasePath : uploadBasePath) + files[index].jobId).put(files[index].file);
    }

    uploadTask.on('state_changed', (snapshot) => {
            //console.log(snapshot);
            files[index].progress = ((snapshot.bytesTransferred / snapshot.totalBytes) * 100).toFixed(2);
        },
        (error) => {
            console.log(error, error.code);
            toast.push(m.failed_to_upload_media_file(), {
                classes: ["toast-danger"]
            });

            /* Update Job Record */
            homeRef.doc(files[index].jobId).update({
                progress: 100,
                statusMsg: error.code,
                status: "Failed",
                completedOn: firebase.firestore.Timestamp.fromDate(new Date()),
                updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
            });

            files[index].registered = 'failed';

            if (index === totalFiles - 1) {
                end();
            } else {
                processJob(index + 1);
            }
        },
        () => {
            // Handle successful uploads on complete
            console.log("TRANSFER COMPLETE");
            if (index === totalFiles - 1) {
                end();
            } else {
                processJob(index + 1);
            }
        }
    );
}

function end() {
    console.log("Ending Batch Transcription");
    toast.push(m.jobs_submitted_successfully(), {
        classes: ["toast-success"]
    });
    showModal("aiTranscriptImport");
}

function resetLanguages() {
    if (provider === "Google Speech-to-Text") {
        language = "en-US";
    } else if (provider === "ElevenLabs Scribe"){
            language = "eng";
    } else {
        language = "en";
    }
}

async function updateProgress(val) {
    progress = parseInt((val / totalFiles) * 100);
}

// Load KNP guides when modal opens (only for team users)
$effect(() => {
    if (modalState.selected === 'batchTranscription' && authState.team?.id && knpState.guides.length === 0) {
        loadKnpGuides();
    }
});
</script>

<div transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'batchTranscription' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="batchTransModalTitle"
    aria-describedby="batchTransModalDesc"
    tabindex="-1" 
    id="batchTranscriptionModal">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="batchTransModalTitle">
                    <i class="bi bi-collection-play me-2"></i>
                    {m.batch_transcription()}
                </h4>
                <button type="button" class="btn-close" id="closeBatchTransModal" aria-label={m.close_dialog()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="batchTransModalDesc" class="visually-hidden">{m.configure_batch_transcription_jobs()}</p>
                
                <div class="container-fluid">
                    <div class="row">
                        <div class="col-md-4">
                            <div class="card shadow-sm mb-4">
                                <div class="card-header bg-light">
                                    <h5 class="card-title mb-0">{m.configuration()}</h5>
                                </div>
                                <div class="card-body">
                                    <div class="mb-4">
                                        <label for="FileInput" class="form-label fw-bold">
                                            <i class="bi bi-file-earmark-music me-2 text-primary"></i>
                                            {m.media_files()}
                                        </label>
                                        <div class="input-group">
                                            <input type="file" class="form-control" 
                                                id="FileInput"
                                                accept="video/*,audio/*" 
                                                onchange={handleFileInput} 
                                                multiple />
                                        </div>
                                        <div class="form-text">{m.select_media_files_help()}</div>
                                    </div>
                                    
                                    <div class="mb-3">
                                        <label class="form-label fw-bold" for="serviceProvider">
                                            <i class="bi bi-hammer me-2 text-primary"></i>
                                            {m.service_provider()}
                                        </label>
                                        <select id="serviceProvider" class="form-select shadow-sm" 
                                            bind:value={provider} 
                                            onchange={resetLanguages}
                                            disabled={processing}
                                            aria-describedby="providerHelp">
                                            <option>Deepgram</option>
                                            <option>Assembly AI</option>
                                            <option>Google Speech-to-Text</option>
                                            <option>Rev AI</option>
                                            <option>Speechmatics</option>
                                            <option>Voicegain</option>
                                            <option>ElevenLabs Scribe</option>
                                        </select>
                                        <div id="providerHelp" class="form-text">{m.select_transcription_service()}</div>
                                    </div>
                                    
                                    <div class="mb-4">
                                        <label class="form-label fw-bold" for="sourceLanguage">
                                            <i class="bi bi-translate me-2 text-primary"></i>
                                            {m.source_language()}
                                        </label>
                                        {#if provider === "Assembly AI"}
                                            <select id="sourceLanguage" class="form-select shadow-sm" disabled={processing} bind:value={language} aria-describedby="languageHelp">
                                                {#each Object.entries(assemblyAiLanguages) as languageOption (languageOption[0])}
                                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                                {/each}
                                            </select>
                                        {:else if provider === "Google Speech-to-Text"}
                                            <select id="sourceLanguage" class="form-select shadow-sm" disabled={processing} bind:value={language} aria-describedby="languageHelp">
                                                {#each Object.entries(googleLanguages) as languageOption (languageOption[0])}
                                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                                {/each}
                                            </select>
                                        {:else if provider === "Deepgram"}
                                            <select id="sourceLanguage" class="form-select shadow-sm" disabled={processing} bind:value={language} aria-describedby="languageHelp">
                                                {#each Object.entries(deepgramLanguages) as languageOption (languageOption[0])}
                                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                                {/each}
                                            </select>
                                        {:else if provider === "Rev AI"}
                                            <select id="sourceLanguage" class="form-select shadow-sm" disabled={processing} bind:value={language} aria-describedby="languageHelp">
                                                {#each Object.entries(revAiLanguages) as languageOption (languageOption[0])}
                                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                                {/each}
                                            </select>
                                        {:else if provider === "Speechmatics"}
                                            <select id="sourceLanguage" class="form-select shadow-sm" disabled={processing} bind:value={language} aria-describedby="languageHelp">
                                                {#each Object.entries(speechmaticsLanguages) as languageOption (languageOption[0])}
                                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                                {/each}
                                            </select>
                                        {:else if provider === "Voicegain"}
                                            <select id="sourceLanguage" class="form-select shadow-sm" disabled={processing} bind:value={language} aria-describedby="languageHelp">
                                                {#each Object.entries(voiceGainLanguages) as languageOption (languageOption[0])}
                                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                                {/each}
                                            </select>
                                        {:else if provider === "ElevenLabs Scribe"}
                                            <select id="sourceLanguage" class="form-select shadow-sm" disabled={processing} bind:value={language} aria-describedby="languageHelp">
                                                {#each Object.entries(elevenLabsScribeLanguages) as languageOption (languageOption[0])}
                                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                                {/each}
                                            </select>
                                        {/if}
                                        <div id="languageHelp" class="form-text">{m.select_source_language()}</div>
                                    </div>
                                    
                                    {#if authState.team?.id}
                                        <div class="mb-4">
                                            <label class="form-label fw-bold" for="knpGuide">
                                                <i class="bi bi-book me-2 text-primary"></i>
                                                {m.knp_terminology_guide_optional()}
                                            </label>
                                            <select
                                                class="form-select shadow-sm"
                                                id="knpGuide"
                                                bind:value={selectedKnpGuide}
                                                disabled={processing}
                                                aria-describedby="knpDescription"
                                            >
                                                <option value={null}>{m.none()}</option>
                                                {#each knpState.guides as guide (guide.id)}
                                                    <option value={guide.id}>{guide.title}</option>
                                                {/each}
                                            </select>
                                            <div id="knpDescription" class="form-text">
                                                {m.knp_guide_description()}
                                            </div>
                                        </div>
                                    {/if}
                                    
                                    {#if provider === "ElevenLabs Scribe"}
                                        <div class="alert alert-warning d-flex align-items-center small" role="alert">
                                            <i class="bi bi-exclamation-triangle-fill fs-5 me-2"></i>
                                            <div>
                                                {m.elevenlabs_scribe_limit_warning()}
                                            </div>
                                        </div>
                                    {/if}
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-md-8">
                            <div class="card shadow-sm">
                                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                                    <h5 class="card-title mb-0">
                                        <i class="bi bi-list-check me-2"></i>
                                        {m.job_queue()}
                                    </h5>
                                    <span class="badge bg-primary rounded-pill">{files.length}</span>
                                </div>
                                <div class="card-body p-0">
                                    <div class="table-responsive">
                                        <table class="table table-striped mb-0">
                                            <thead class="table-light">
                                                <tr>
                                                    <th>{m.job_id()}</th>
                                                    <th>{m.file_name()}</th>
                                                    <th class="text-center">{m.status()}</th>
                                                    <th>{m.upload_progress()}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {#each files as file (file.name)}
                                                <tr>
                                                    <td class="text-nowrap">
                                                        <code class="small">{file.jobId || m.pending()}</code>
                                                    </td>
                                                    <td class="text-truncate" style="max-width: 200px;" title={file.name}>
                                                        <i class="bi bi-file-earmark-play me-2 text-secondary"></i>
                                                        {file.name}
                                                    </td>
                                                    <td class='text-center'>
                                                        {#if file.registered === "done"}
                                                            <span class="badge bg-success">
                                                                <i class="bi bi-check-lg me-1"></i>
                                                                {m.registered()}
                                                            </span>
                                                        {:else if file.registered === "in progress"}
                                                            <span class="badge bg-info">
                                                                <i class="bi bi-hourglass-split me-1"></i>
                                                                {m.processing()}
                                                            </span>
                                                        {:else if file.registered === "failed"}
                                                            <span class="badge bg-danger">
                                                                <i class="bi bi-x-lg me-1"></i>
                                                                {m.failed()}
                                                            </span>
                                                        {:else}
                                                            <span class="badge bg-secondary">
                                                                <i class="bi bi-clock me-1"></i>
                                                                {m.queued()}
                                                            </span>
                                                        {/if}
                                                    </td>
                                                    <td style="width: 30%;">
                                                        <div class="progress" style="height: 10px;">
                                                            <div class="progress-bar 
                                                                {file.registered === 'failed' ? 'bg-danger' : 
                                                                file.progress === 100 ? 'bg-success' : 'bg-info'}" 
                                                                style="width: {file.progress || 0}%;">
                                                            </div>
                                                        </div>
                                                        <span class="small text-muted">{file.progress || 0}%</span>
                                                    </td>
                                                </tr>
                                                {:else}
                                                <tr>
                                                    <td colspan="4" class="text-center py-5 text-muted">
                                                        <i class="bi bi-cloud-arrow-up fs-1 mb-3 d-block"></i>
                                                        <p>{m.no_media_files_selected()}</p>
                                                        <p class="small text-muted">{m.select_files_left_to_begin()}</p>
                                                    </td>
                                                </tr>
                                                {/each}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                {#if processing}
                                <div class="card-footer bg-light py-2">
                                    <div class="alert alert-info d-flex align-items-center mb-0 py-2 small" role="alert">
                                        <div class="spinner-border spinner-border-sm text-info me-2" role="status" aria-hidden="true"></div>
                                        <div>
                                            {m.wait_until_processing_complete()}
                                        </div>
                                    </div>
                                </div>
                                {:else if files && files.length > 0}
                                <div class="card-footer bg-light py-2">
                                    <div class="alert alert-primary d-flex align-items-center mb-0 py-2 small" role="alert">
                                        <i class="bi bi-info-circle-fill me-2"></i>
                                        <div>
                                            {m.click_submit_when_ready({ count: files.length })}
                                        </div>
                                    </div>
                                </div>
                                {/if}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <div class="container-fluid">
                    <div class="row align-items-center">
                        <div class="col">
                            <div class="d-flex align-items-center">
                                <div class="me-2 small text-muted" style="min-width: 40px;">
                                    {progress}%
                                </div>
                                <div class="progress flex-grow-1" role="progressbar" 
                                    aria-valuemin="0" 
                                    aria-valuemax="100" 
                                    aria-valuenow={progress}>
                                    <div class="progress-bar progress-bar-striped progress-bar-animated" 
                                        style="width: {progress}%;"></div>
                                </div>
                            </div>
                        </div>
                        <div class="col-auto">
                            <button class="btn btn-primary ms-2" id="submitBatchBtn" type="button" 
                                disabled={processing || !files || files.length === 0} 
                                onclick={() => start()}
                                aria-busy={processing}>
                                <i class="bi bi-cloud-upload me-2"></i>
                                {files.length !== 1 ? m.submit_jobs({ count: files.length }) : m.submit_job({ count: files.length })}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
.table-responsive {
    max-height: 50vh;
}

.table th, .table td {
    padding: 0.75rem;
    vertical-align: middle;
}

.table tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.card {
    border-radius: 0.375rem;
    border: 1px solid rgba(0, 0, 0, 0.125);
    overflow: hidden;
}

.modal-content {
    border-radius: 0.5rem;
}

.badge {
    font-weight: 500;
    padding: 0.4em 0.6em;
}

code {
    background-color: #f8f9fa;
    padding: 0.2rem 0.4rem;
    border-radius: 0.2rem;
    color: #212529;
}

.form-select, .form-control {
    border-radius: 0.375rem;
}

.progress {
    height: 0.75rem;
    border-radius: 1rem;
    background-color: #e9ecef;
}
</style>
